import React from "react";
import { FileText, ExternalLink } from "lucide-react";

/**
 * Iter-387 — legacy car migrations attach report PDFs (e.g. Consumer
 * Information Notices) as `documents: [{ name, data }]` (base64 data-URI);
 * browsers block top-frame `data:` navigation so those open via a Blob.
 * Iter 736 — new ads return `documents: [{ name, url, size }]` where
 * `url` is the backend streaming proxy serving a real PDF with the right
 * filename — those are a plain link, no blob needed.
 */
function openDataDocument(doc) {
  const [meta, b64] = String(doc.data || "").split(",");
  if (!b64) return;
  const mime = meta.match(/^data:([^;]+)/)?.[1] || "application/pdf";
  const bytes = atob(b64);
  const buf = new Uint8Array(bytes.length);
  for (let i = 0; i < bytes.length; i++) buf[i] = bytes.charCodeAt(i);
  const url = URL.createObjectURL(new Blob([buf], { type: mime }));
  window.open(url, "_blank", "noopener");
  setTimeout(() => URL.revokeObjectURL(url), 60_000);
}

function fmtSize(bytes) {
  if (!bytes) return null;
  const mb = bytes / (1024 * 1024);
  return mb >= 1 ? `${mb.toFixed(1)} MB` : `${Math.max(1, Math.round(bytes / 1024))} KB`;
}

export default function ListingDocuments({ listing }) {
  const docs = (listing?.documents || []).filter((d) => d?.url || d?.data);
  if (!docs.length) return null;
  return (
    <section data-testid="ad-documents-section" className="mt-6 rounded-2xl border border-slate-200 bg-white shadow-card p-5">
      <h2 className="font-display text-lg font-bold text-slate-800 mb-3">Documents</h2>
      <ul className="space-y-2">
        {docs.map((doc, i) => {
          const inner = (
            <>
              <span className="shrink-0 inline-flex items-center justify-center w-9 h-9 rounded-lg bg-red-50 border border-red-100">
                <FileText size={18} className="text-red-600" strokeWidth={2.2} />
              </span>
              <span className="min-w-0 flex-1">
                <span className="block text-sm font-bold text-slate-800 truncate group-hover:text-[color:var(--brand-blue)]">
                  {doc.name || `Document ${i + 1}`}
                </span>
                <span className="block text-[11px] text-slate-500 uppercase tracking-widest font-semibold">
                  PDF{fmtSize(doc.size) ? ` · ${fmtSize(doc.size)}` : ""}
                </span>
              </span>
              <ExternalLink size={15} className="shrink-0 text-slate-400 group-hover:text-[color:var(--brand-blue)]" />
            </>
          );
          const cls =
            "w-full flex items-center gap-3 rounded-xl border border-slate-200 bg-slate-50 hover:bg-white hover:border-[color:var(--brand-blue)] hover:shadow-sm transition-colors px-3.5 py-2.5 text-left group";
          return (
            <li key={i}>
              {doc.url ? (
                <a
                  href={doc.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid={`ad-document-item-${i}`}
                  className={cls}
                >
                  {inner}
                </a>
              ) : (
                <button
                  type="button"
                  data-testid={`ad-document-item-${i}`}
                  onClick={() => openDataDocument(doc)}
                  className={cls}
                >
                  {inner}
                </button>
              )}
            </li>
          );
        })}
      </ul>
    </section>
  );
}
